//-- Copyright (c) 2021 TMT Insights Group, LLC
import { OrderPlacementConfig } from '@/__configs/config_types';
import { getLineageMetaTitle, insertWordBreaks } from '@tmt-insights/oms_fe_core/utils';
import { CustomerProfile, InventoryFileEntity, OrderJob, TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import styled from '@emotion/styled';
import { Box, Grid, IconButton, Tooltip, Typography } from '@tmt-insights/oms_fe_core/material';
import {
  ConditionalTooltip,
  DateTimeDisplay,
  ImportantTags,
  JobTypeLabel,
  LabelValue,
  PolarisIcon,
  Thumbnail,
  TitleTypeLabel,
  TitleVersionChip
} from '@tmt-insights/oms_fe_core/columns';

const orderPlacementConfig: OrderPlacementConfig = {
  shouldHideHiddenProfiles: true,
  totalFilesSizeWarningThreshold: 600 * 1024 ** 3, // 600 GB

  entityTypes: [],
  // the custom fields that are displayed in steps 1 and 3
  addlFields: {
    orderDetails: {
      po_number: {
        label: 'Order Number',
        id: 'po_number'
      },
      billable_customer: {
        label: 'Client Name',
        id: 'billable_customer'
      },
      budget_code: {
        label: 'Budget Code'
      },
      billing_code: {
        label: 'Billing Code'
      }
    }
  },
  // the title display box under the title search on step 2 in both flows
  viewingTitleHeader: (title: TitleEntity) => {
    const lineageMetaTitle = getLineageMetaTitle(title);
    return (
      <>
        <Typography variant="h6" sx={{ mb: 0 }}>
          <strong>{title.lineage_meta.series_name}</strong>
        </Typography>
        <Typography variant="h6" sx={{ mb: 1, opacity: 0.7, fontSize: '1.2rem', lineHeight: 1.3 }}>
          {lineageMetaTitle}
        </Typography>
        <Grid container justifyContent="space-between" alignItems="flex-end">
          <div>
            <Grid container sx={{ mb: 1 }}>
              <TitleTypeLabel type={title.entity_type} />
              <Typography variant="caption" sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>
                Prog ID: {title.program_id}
              </Typography>
            </Grid>
          </div>
        </Grid>
      </>
    );
  },
  // the title search result item
  titleSearchResult: (result: TitleEntity) => {
    const series = result?.lineage_meta?.series_name;
    const seasonNum = result?.lineage_meta?.season_sequence_order;
    const episodeNum = result?.sequence_order;
    return (
      <Grid container>
        <Thumbnail src={result?.thumbnail_link || ''} />
        <Grid item sx={{ ml: 1.5 }}>
          <div>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0 }}>{series}</Typography>
            <Typography variant="caption">
              <span>
                {seasonNum ? `S${seasonNum} ` : ''}
                {episodeNum ? `E${episodeNum}` : ''}
              </span>
              {seasonNum || episodeNum ? <span style={{ opacity: 0.5 }}> &bull; </span> : ''}
              {result?.name}
            </Typography>
          </div>
        </Grid>
        <Grid item sx={{ ml: 'auto', textAlign: 'right' }}>
          <TitleTypeLabel width="auto" type={result?.entity_type} sx={{ ml: 'auto' }} />
          <div style={{ marginTop: 4 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              PROG ID:<strong> {result?.program_id}</strong>
            </Typography>
          </div>
        </Grid>
      </Grid>
    );
  },
  // advanced order step 2, the header of a grouped output job/version
  outputHeader: (group: any, outputVersionObj: TitleEntity, profileEditType: string) => {
    const editType = group.title_inputs?.edit_type || profileEditType;
    const outputName = outputVersionObj?.meta?.usage || 'Core';
    const series = outputVersionObj?.lineage_meta?.series_name;
    const lineageMetaTitle = getLineageMetaTitle(outputVersionObj);
    const bullet = <span style={{ padding: '0 8px', opacity: 0.5, fontSize: '.9rem', translate: '0 1px' }}>&bull;</span>;

    if (!group.primary_version) return null;
    return (
      <Grid container alignItems="center">
        {editType && (
          <>
            <Typography sx={{ color: 'primary.main', fontWeight: 'bold', fontFamily: 'inherit' }}>{editType.toUpperCase()}</Typography>
            {bullet}
          </>
        )}
        <strong>{series}</strong>
        {lineageMetaTitle ? bullet : ''}
        {lineageMetaTitle && <span style={{ opacity: 0.7 }}>{lineageMetaTitle.replace(/:[^.]*$/, '')}</span>}
        {bullet}
        <strong>{outputName}</strong>
        {outputVersionObj?.external_id && <span style={{ opacity: 0.7, paddingLeft: 4 }}>[{outputVersionObj.external_id}]</span>}
      </Grid>
    );
  },
  // the dropdown option for the version picker, used in order placement and title ui
  versionDropdownOption: (option: TitleEntity) => {
    if (!option) return null;
    const label = option.sub_type === 'core_versions' ? 'Version' : 'Piece';
    const catCode = option?.meta?.master_category_code;
    const bullet = <span style={{ padding: '0 4px', opacity: 0.5, fontSize: '.9rem' }}>&bull;</span>;

    return (
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        flexWrap="nowrap"
        sx={{ '& .link': { opacity: 0 }, '&:hover .link': { opacity: 0.6 } }}
      >
        <Grid container item flexDirection="column">
          <div>
            <strong>{option.meta?.usage || 'Core'}</strong>
            {!!catCode && (
              <>
                {bullet}
                {catCode}
              </>
            )}{' '}
          </div>
          <Typography component="span" sx={{ fontSize: '.8rem', opacity: 0.7 }}>
            {label} {option.meta?.friendly_id || option.display_id}
            {option.piece_number ? ` / ${option.piece_number}` : ''}
          </Typography>
        </Grid>
        <div style={{ height: 17, display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', columnGap: 2 }}>
          <IconButton
            onClick={() => window.open(`${window.origin}/titles/${option.program_id}/?tab=metadata&version_id=${option.external_id}`, '_blank')}
            disableRipple
            sx={{ '&:hover': { background: 'none' } }}
            className="link"
          >
            <PolarisIcon icon="external_link" />
          </IconButton>
          {!!option.notes && (
            <Tooltip title={option.notes} key={option.external_id} sx={{ fontFamily: 'DM Sans' }}>
              <PolarisIcon icon="note" height={18} style={{ opacity: 0.6 }} />
            </Tooltip>
          )}
        </div>
      </Grid>
    );
  },
  // the selected option for the version picker, used in order placement and title ui
  versionOptionLabel: (option: TitleEntity) => {
    if (!option) return null;
    const label = option.sub_type === 'core_versions' ? 'Version' : 'Piece';
    const catCode = option?.meta?.master_category_code;
    return `${option?.meta?.usage || 'Core'}${catCode ? ` • ${catCode}` : ''} [${label} ${option.meta?.friendly_id || option.display_id}${
      option.piece_number ? ` / ${option.piece_number}` : ''
    }]`;
  },
  // the title of the pending files selections when grouped by version (files from 2 or more versions selected)
  fileSelectionTitle: (version: TitleEntity | TitleEntityV2) => {
    return (
      <Grid container justifyContent="space-between" flexWrap="nowrap">
        <div>
          <strong>
            {version?.lineage_meta?.series_name} {getLineageMetaTitle(version)}
          </strong>
        </div>
      </Grid>
    );
  },
  // on simple flow step 2, the episode can be expanded to show more info about each version
  expandedVersionRows: (versions: TitleEntity[]) => {
    const StyledRow = styled.tr`
      td {
        padding: ${window.theme.spacing(1)};
      }
    `;

    return (
      <Box
        sx={{
          width: '100%',
          '& tr td:first-of-type': { borderRadius: '4px 0 0 4px' },
          '& tr td:last-of-type': { borderRadius: '0 4px 4px 0' },
          '& tr td': { transition: 'ease background-color 300ms' },
          '& tr:hover td': {
            backgroundColor: window.theme.palette.action.focus,
            transition: 'ease background-color 300ms'
          },
          '& tr .link': {
            opacity: 0
          },
          '& tr:hover .link': {
            opacity: 0.8,
            transition: 'opacity 300ms ease', // Change transition property
            cursor: 'pointer'
          }
          // '& .MuiTypography-root': {
          //   fontFamily: window.theme.typography.fontFamily
          // }
        }}
      >
        <hr style={{ opacity: 0.3, color: window.theme.palette.divider, marginBottom: window.theme.spacing(2), marginTop: 0, borderBottom: 0 }} />
        <div style={{ width: '100%', overflowY: 'auto' }}>
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {versions?.map((version) => {
                return (
                  <StyledRow key={version.id}>
                    <td style={{ lineHeight: 1 }}>
                      <Typography sx={{ fontWeight: 'bold', opacity: 0.9 }}>{version.meta?.usage}</Typography>
                      {version.meta?.version_type ? (
                        <Typography>{version.meta?.version_type}</Typography>
                      ) : (
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                          Unspecified
                        </Typography>
                      )}
                    </td>
                    <td>
                      <Typography variant="caption" component="div" sx={{ opacity: 1, lineHeight: 1 }}>
                        {version.meta?.is_core === 'true' || !!version.meta?.is_core ? 'v' : ''}
                        {version.meta?.version_number || version.piece_id}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1, fontSize: 10 }}>
                        {version.meta?.piece_number || version.piece_number}
                      </Typography>
                    </td>
                    <td style={{ width: '100%' }}>
                      {version.meta?.name ? (
                        <Typography variant="caption" sx={{ pr: 1, opacity: 0.8, display: 'inline-flex', wordBreak: 'break-word' }}>
                          {insertWordBreaks(version.meta.name)}
                          <div style={{ flexBasis: 'fit-content', marginLeft: 5 }}>
                            <ImportantTags tags={version.meta.important_tags} iconOnly />
                          </div>
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ fontStyle: 'italic', pr: 1 }}>
                          Not created yet
                        </Typography>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: 3 }}>
                      <TitleVersionChip key={version.id} version={version} sx={{ minWidth: 80 }} />
                    </td>
                    <td style={{ padding: 0, paddingRight: 5 }}>
                      <Box sx={{ m: 0.5 }}>
                        <IconButton
                          onClick={() => {
                            window.open(`${window.origin}/titles/${version.program_id}/?tab=metadata&version_id=${version.external_id}`, '_blank');
                          }}
                          className="link"
                        >
                          <PolarisIcon icon="external_link" height={18} style={{ color: window.theme.palette.text.primary }} />
                        </IconButton>
                      </Box>
                    </td>
                    <td style={{ padding: 0 }}>
                      <ConditionalTooltip title={version.notes} key={version.external_id} show={version.notes?.length}>
                        <Box sx={{ width: 18, mr: 1, display: 'flex', alignItems: 'center' }}>
                          <PolarisIcon icon="note" height={18} style={{ opacity: !version.notes?.length ? 0.2 : 0.6 }} />
                        </Box>
                      </ConditionalTooltip>
                    </td>
                  </StyledRow>
                );
              })}
            </tbody>
          </table>
        </div>
      </Box>
    );
  },
  // the tooltip that's displayed when hovering over a version chip -- step 2 of simple flow
  versionChipTooltip: (version: TitleEntity) => {
    return (
      <div>
        <strong>{version.meta?.is_core === 'true' || !!version.meta?.is_core ? 'Version' : 'Piece'} ID: </strong>
        {version.meta?.is_core === 'true' || !!version.meta?.is_core ? 'v' : ''}
        {version.meta?.version_number || version.piece_id}
      </div>
    );
  },

  parseVersionsFn: (versions: TitleEntity[]) => {
    const alphabetizedVersions = versions
      .map((version) => ({
        ...version,
        id: version.external_id || version.id,
        original_id: version.id,
        version_id: version.version_external_id,
        display_id: version.external_id,
        disabled: !version.rally_id && version.sub_type?.toLowerCase() === 'pieces', // disables future ordering
        root_parent: version.parent,
        displayed_name: (version.meta?.friendly_name || version.name)?.toLowerCase()
      }))
      .sort((a, b) => (a.displayed_name > b.displayed_name ? 1 : -1));
    const disabledVersions = alphabetizedVersions.filter((version) => version.disabled);
    const enabledVersions = alphabetizedVersions.filter((version) => !version.disabled);
    return [...enabledVersions, ...disabledVersions] as TitleEntity[];
  },
  parseFilesFn: (files: InventoryFileEntity[]) => files.map((file) => ({ ...file, version_id: file?.version_external_id || file?.version_id })),
  parseTitlesFn: (titles) => {
    return titles.map(
      (title) =>
        ({
          ...title,
          og_entity_type: title.entity_type,
          entity_type: title.program_type
        }) as TitleEntity
    );
  },
  getLineageMetaTitle: (title: TitleEntity | undefined) => {
    if (!title || !title.lineage_meta || !Object.keys(title.lineage_meta).length) return '';
    const meta = title.lineage_meta;
    const season_sequence_order = meta.season_sequence_order;
    const isEpisode = ['program', 'episode'].includes(title.og_entity_type || title.entity_type) || meta.program_sequence_order || meta.episode_name;
    const isMovie = meta.movie_name;
    const movieName = meta.movie_name;
    const isOVA = meta.ova_name;
    const ovaName = meta.ova_name;
    const epNum = meta.program_sequence_order || title.sequence_order || meta.episode_sequence_order;
    const epNumDisplay = isEpisode && epNum ? `E${isNaN(Number(epNum)) ? epNum : Number(epNum)}` : '';
    const episodeName = meta.episode_name || meta.program_name || meta.episode_title || title.name;
    const seriesName = meta.series_name || '';
    const seasonNum = season_sequence_order ? `S${season_sequence_order}${epNumDisplay ? ' ' : ''}` : '';

    // we set display_title on AE, so swap series/episode bc they want that
    // HOWEVER something here is still overengineered so TODO: fix this
    if (isEpisode || title.display_title) {
      return `${seriesName} ${seasonNum}${epNumDisplay}${seasonNum || epNumDisplay ? ':' : ''} ${episodeName}`;
    } else if (isMovie) {
      return `${seriesName ? seriesName + ' ' : ''}${movieName}`;
    } else if (isOVA) {
      return `${seriesName ? seriesName + ' ' : ''}${ovaName}`;
    } else {
      return `${seasonNum}${epNumDisplay}${seasonNum || epNumDisplay ? ':' : ''} ${seriesName}`;
    }
  },

  bulkOrderJobDisplay: (job: OrderJob, title: any) => {
    return (
      <Grid container alignItems="center">
        <Grid item sm={1}>
          <strong>{job.hid}</strong>
        </Grid>
        <Grid item sm={1.5}>
          <JobTypeLabel type={job.job_type} />
        </Grid>
        <Grid item sm={3}>
          <Typography variant="body1" style={{ fontWeight: 'bold' }}>
            {job.title.lineage_meta?.series_name}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {title}
          </Typography>
        </Grid>
        <Grid item sm={1.25}>
          <LabelValue label="Embargo Date">
            <DateTimeDisplay missingText="-" singleLine>
              {job.embargo_date}
            </DateTimeDisplay>
          </LabelValue>
        </Grid>
        <Grid item sm={1.25}>
          <LabelValue label="Due Date">
            <DateTimeDisplay missingText="-" singleLine>
              {job.due_date}
            </DateTimeDisplay>
          </LabelValue>
        </Grid>
        <Grid item sm={1.25}>
          <LabelValue label="Program ID">{job.title?.program_id || '-'}</LabelValue>
        </Grid>
        <Grid item sm={1.25}>
          <LabelValue label="Piece ID">{job.piece_id || '-'}</LabelValue>
        </Grid>
        <Grid item sm={1.25}>
          <LabelValue label="Piece Number">{job.piece_number || '-'}</LabelValue>
        </Grid>
      </Grid>
    );
  },
  findDefaultVersion: (versions: TitleEntity[], { profile }: { profile: CustomerProfile }) => {
    if (profile?.data?.workflow_type === 'component_based') {
      const sortedVersions = versions
        .filter((v) => v?.meta?.master_category_code?.toLowerCase() === 'crs')
        .sort((a, b) => {
          const aId: string = a?.meta?.friendly_id || '';
          const bId: string = b?.meta?.friendly_id || '';

          // Split by underscore and convert each part to number
          const aParts = aId.split('_').map((part) => parseInt(part, 10) || 0);
          const bParts = bId.split('_').map((part) => parseInt(part, 10) || 0);

          // Compare each part from left to right
          for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;

            if (aPart !== bPart) {
              return bPart - aPart; // Descending order (highest first)
            }
          }

          return 0; // Equal
        });
      if (sortedVersions.length > 0) {
        return sortedVersions[0];
      }
    }

    return versions?.[0];
  }
};

export default orderPlacementConfig;
